import PropTypes from 'prop-types';
import {
  CardHeader,
  Row,
  GgxNumber,
  OrderStatusDesc,
  OrderStatusType,
  StatusIcon,
  TrackingCard,
  TitleRow,
  ContentCollapse,
  CustomDivider,
  ExpandIcon,
  HeaderTime,
  MobileVerticle,
  ProgressIcon,
  CustomStepLabel,
  CustomStepMobileContent,
  mapStyles,
} from '@/modules/track-and-trace/track-record-card/track-record-card.styles';
import { useEffect, useState } from 'react';
import { displayTimeStringWithoutYear } from '@/utilities/reusable-functions/display-util';
import TrackRecordRow from '@/modules/track-and-trace/track-record-row/track-record-row.component';
import InTransitSvg from '@/assets/in-transit-filled.svg';
import { Stepper } from '@gogovan/entier-web-core/ext/mui/Stepper';
import { Step } from '@gogovan/entier-web-core/ext/mui/Step';
import { StepConnector } from '@gogovan/entier-web-core/ext/mui/StepConnector';
import { useMediaQuery } from '@gogovan/entier-web-core/ext/mui/useMediaQuery';
import { setupGlobalTimeZone } from '@gogovan/entier-web-core/utilities/date-time-util';
import { tntTheme as theme } from '@/utilities/theme';
import { WorkflowStatusType } from '@gogovan/entier-web-core/enums/workflow-status-type.enum';
import { Box } from '@gogovan/entier-web-core/ext/mui/Box';
import dynamic from 'next/dynamic';
import { Skeleton } from '@gogovan/entier-web-core/ext/mui/Skeleton';

const Map = dynamic(() => import('@/modules/track-and-trace/track-record-card/map/map.component'), {
  ssr: false,
  loading: () => <Skeleton variant='rectangular' height={300} sx={{ margin: '16px 16px 0 16px' }} />,
});

const statusColorMapping = {
  [WorkflowStatusType.CREATED]: '#0088BF',
  [WorkflowStatusType.USER_DEFINED]: '#0088BF',
  [WorkflowStatusType.EXCEPTION]: '#EF5350',
  [WorkflowStatusType.DELIVERED]: '#4CAF50',
  [WorkflowStatusType.COMPLETED]: '#B4B4B4',
  [WorkflowStatusType.CANCELLED]: '#B4B4B4',
};

const TrackRecordCard = ({ trackingRecords, isLoading, setMapDialogInfo }) => {
  const [orderStatusIcon, setOrderStatusIcon] = useState(<div />);
  const [openDetail, setOpenDetail] = useState(false);
  const firstTrackingRecord = trackingRecords[0] || {};
  const {
    trackingInfo,
    deliveryAddressInfo = {},
    stateType,
    timezone,
    orderId,
    statusType,
    description,
    createDateTime,
  } = firstTrackingRecord;
  const { lat: destLat, lon: destLon } = deliveryAddressInfo;
  const shiftTrackingRecords = JSON.parse(JSON.stringify(trackingRecords));
  const isWindowSize = useMediaQuery(theme.breakpoints.up('sm'));
  shiftTrackingRecords.shift();

  const latestStatusIconMapping = (type) => {
    const statusIconColor = statusColorMapping[type] || '#0088BF';
    if (type === WorkflowStatusType.DELIVERED)
      setOrderStatusIcon(<StatusIcon color={statusIconColor}>check_circle</StatusIcon>);
    else if (type === WorkflowStatusType.EXCEPTION)
      setOrderStatusIcon(<StatusIcon color={statusIconColor}>error</StatusIcon>);
    else if ([WorkflowStatusType.COMPLETED, WorkflowStatusType.CANCELLED].includes(type))
      setOrderStatusIcon(<StatusIcon color={statusIconColor}>cancel</StatusIcon>);
    else
      setOrderStatusIcon(
        <StatusIcon>
          <InTransitSvg />
        </StatusIcon>
      );
  };

  useEffect(() => {
    latestStatusIconMapping(stateType);
    setupGlobalTimeZone(timezone);
  }, [stateType, timezone]);

  return (
    <TrackingCard>
      {trackingInfo && (
        <Map
          isLoading={isLoading}
          destination={
            destLat && destLon
              ? {
                  lat: Number(destLat),
                  lon: Number(destLon),
                }
              : null
          }
          driver={trackingInfo}
          setMapDialogInfo={setMapDialogInfo}
          sx={mapStyles}
        />
      )}

      <CardHeader
        canClick={shiftTrackingRecords.length > 0}
        onClick={() => {
          setOpenDetail(!openDetail);
        }}
      >
        <Row>
          {orderStatusIcon}
          <MobileVerticle>
            <Box sx={{ width: '100%' }}>
              <TitleRow>
                <GgxNumber>{orderId}</GgxNumber>
                <OrderStatusType color={statusColorMapping[stateType] || '#0088BF'}>{statusType}</OrderStatusType>
              </TitleRow>
              <OrderStatusDesc>{description}</OrderStatusDesc>
            </Box>
            <HeaderTime>{displayTimeStringWithoutYear(new Date(createDateTime))}</HeaderTime>
          </MobileVerticle>
        </Row>
        {shiftTrackingRecords.length > 0 && <ExpandIcon openDetail={openDetail}>expand_more</ExpandIcon>}
      </CardHeader>
      {shiftTrackingRecords.length > 0 && (
        <ContentCollapse in={openDetail} timeout='auto' unmountOnExit>
          <CustomDivider />
          <Stepper orientation='vertical'>
            <StepConnector sx={{ height: '0px' }} />
            {shiftTrackingRecords.map((trackingRecord, index) => (
              <Step active>
                {isWindowSize ? (
                  <CustomStepLabel icon={<ProgressIcon />}>
                    {/* eslint-disable-next-line react/no-array-index-key */}
                    <TrackRecordRow trackingRecord={trackingRecord} key={index} />
                  </CustomStepLabel>
                ) : (
                  <>
                    <CustomStepLabel icon={<ProgressIcon />}>
                      {/* eslint-disable-next-line react/no-array-index-key */}
                      <TrackRecordRow trackingRecord={trackingRecord} key={index} noContent />
                    </CustomStepLabel>
                    <CustomStepMobileContent>
                      {/* eslint-disable-next-line react/no-array-index-key */}
                      <TrackRecordRow trackingRecord={trackingRecord} key={index} noHeader />
                    </CustomStepMobileContent>
                  </>
                )}
              </Step>
            ))}
          </Stepper>
        </ContentCollapse>
      )}
    </TrackingCard>
  );
};

TrackRecordCard.propTypes = {
  trackingRecords: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  setMapDialogInfo: PropTypes.func.isRequired,
};

export default TrackRecordCard;
